import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import {
  RefreshCw, Filter, Edit3, Stethoscope, Building2, Calendar as CalendarIcon, Loader2, Hash, Search, X, CircleDot, Camera, Eye, Lock, Trash2, CheckCircle2,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { getStagePermission } from "@/utils/stagePermissions";
import { toast } from "@/components/ui/sonner";

const API = window.location.origin + "/api";

// Pipeline → mode mapping (must mirror REPARTI_CONFIG in App.js)
const PIPELINE_TO_MODE = {
  // New pipelines (Feb-May 2026 migration)
  "529iRmd3Wh7bjdgfHpP9": "clinico",     // Assicurazioni - Reparto Clinico - 01
  "Eu6Xz0GLvHfN5PejJ4SV": "ortodonzia",  // Assicurazioni - Ortodonzia - 01
  "I2wcBQKe3y6DhTYfFySx": "clinico",     // Promemoria - Reparto Clinico 01
  "l191W5Qj1vs4QSEc9xVd": "ortodonzia",  // Promemoria - Ortodonzia 01
  // Indiretti (pazienti senza assicurazione)
  "he2K2EvoAaa3SxaikphM": "clinico",     // Indiretti - Reparto Clinico
  "YNar5ZkxXDvFp31UMmHY": "ortodonzia",  // Indiretti - Ortodonzia
  // Legacy pipelines kept for already-created opportunities
  "tmyDM7u0ygOPtn9rYMQT": "clinico",     // Reparto Clinico (legacy)
  "cVQGQNimL45cGp3OBsAD": "clinico",     // Promemoria Appuntamento (legacy)
  "MyxgABpCKYCkMRBoQMDc": "ortodonzia",  // Ortodonzia (legacy)
  "T2q5AK85MGrZBv1z0nQy": "ortodonzia",  // Promemoria Ortodonzia (legacy)
};

// Pipeline indirette = paziente SENZA assicurazione. Riconosciute in UI
// per badge "Indiretto" e gestione costo Base = costo cliente.
const INDIRETTI_PIPELINES = new Set([
  "he2K2EvoAaa3SxaikphM", // Indiretti - Reparto Clinico
  "YNar5ZkxXDvFp31UMmHY", // Indiretti - Ortodonzia
]);

const isIndiretto = (pid) => INDIRETTI_PIPELINES.has(pid);

const repartoLabel = (pipelineId) => ({
  "529iRmd3Wh7bjdgfHpP9": "Reparto Clinico",
  "Eu6Xz0GLvHfN5PejJ4SV": "Ortodonzia",
  "I2wcBQKe3y6DhTYfFySx": "Promemoria Appuntamento",
  "l191W5Qj1vs4QSEc9xVd": "Promemoria Ortodonzia",
  "he2K2EvoAaa3SxaikphM": "Reparto Clinico — Indiretto",
  "YNar5ZkxXDvFp31UMmHY": "Ortodonzia — Indiretto",
  "tmyDM7u0ygOPtn9rYMQT": "Reparto Clinico (legacy)",
  "cVQGQNimL45cGp3OBsAD": "Promemoria Appuntamento (legacy)",
  "MyxgABpCKYCkMRBoQMDc": "Ortodonzia (legacy)",
  "T2q5AK85MGrZBv1z0nQy": "Promemoria Ortodonzia (legacy)",
}[pipelineId] || "—");

const fmtEuro = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

// Pipelines visible in the archive: "Piano di Cura" + Indiretti.
// Promemoria (and any legacy auxiliary pipelines) are filtered out so the
// page stays focused on treatment plans + cash patients.
const ARCHIVIO_PIPELINE_IDS = new Set([
  "529iRmd3Wh7bjdgfHpP9", // Assicurazioni - Reparto Clinico - 01
  "Eu6Xz0GLvHfN5PejJ4SV", // Assicurazioni - Ortodonzia - 01
  "he2K2EvoAaa3SxaikphM", // Indiretti - Reparto Clinico
  "YNar5ZkxXDvFp31UMmHY", // Indiretti - Ortodonzia
  "tmyDM7u0ygOPtn9rYMQT", // Reparto Clinico (legacy)
  "MyxgABpCKYCkMRBoQMDc", // Ortodonzia (legacy)
]);

// Stage in cui un piano è ancora modificabile. Appena l'opportunità passa
// allo stage successivo, l'archivio nasconde l'icona pennino (sola lettura
// via Eye). Vale solo per le pipeline "Assicurazioni": Indiretti /
// Promemoria / legacy restano sempre modificabili. Backend duplica la
// stessa lista (server.py · EDITABLE_STAGE_NAMES_BY_PIPELINE).
//
// Match per NOME stage (case-insensitive) anziché per id GHL: i nomi sono
// stabili, gli id cambiano se gli stages vengono ricreati su GHL.
const EDITABLE_STAGE_NAMES_BY_PIPELINE = {
  // Assicurazioni - Reparto Clinico - 01
  "529iRmd3Wh7bjdgfHpP9": new Set([
    "da autorizzare",
    "caricati",
    "caricati da paziente",
  ]),
  // Assicurazioni - Ortodonzia - 01
  "Eu6Xz0GLvHfN5PejJ4SV": new Set([
    "preventivi da caricare",
    "preventivi caricati",
    "caricati da paziente",
  ]),
};

const isPianoEditable = (pipelineId, liveStageName) => {
  const allowed = EDITABLE_STAGE_NAMES_BY_PIPELINE[pipelineId];
  if (!allowed) return true; // pipeline non vincolata
  return allowed.has((liveStageName || "").trim().toLowerCase());
};

// Lista più ampia di `EDITABLE_STAGE_NAMES_BY_PIPELINE`: include sia
// "Da autorizzare"/"Preventivi da caricare" sia "Caricati"/"Preventivi
// caricati" sia "Caricati da Paziente". Match case-insensitive sul
// live_stage_name (più robusto se gli stage IDs vengono ricreati su GHL).
// Feb 2026: aggiunto "caricati da paziente" su richiesta utente — la
// duplicazione deve essere possibile anche dopo che il paziente ha
// caricato il preventivo.
// Helpers `DUPLICATE_TO_INDIRETTI_STAGE_NAMES` e `canDuplicateToIndiretti`
// RIMOSSI il 04/06/2026: il flusso "duplica → Indiretti" è stato sostituito
// dallo split inline nel piano di cura editabile (cfr. App.js +
// Odontogram.jsx). Vedi commit history per la logica originale di gating
// sul live_stage_name.

export default function OpportunitaCreatePage() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const today = format(new Date(), "yyyy-MM-dd");
  // I filtri della pagina sono persistiti in sessionStorage con la chiave
  // `archiveFilters` così quando l'utente apre un piano in Aggiorna/Consulta
  // e poi torna indietro (via X o post-save) la lista si ripresenta con
  // gli stessi filtri da cui era partito. Anche un refresh della pagina
  // mantiene il contesto finché la tab resta aperta (sessionStorage).
  const ARCHIVE_FILTERS_KEY = "archiveFilters";
  const restoreFilters = () => {
    try {
      const raw = sessionStorage.getItem(ARCHIVE_FILTERS_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };
  const saved = restoreFilters();
  const [date, setDate] = useState(saved?.date ?? today);
  const [doctorFilter, setDoctorFilter] = useState(saved?.doctorFilter ?? "");
  const [insuranceFilter, setInsuranceFilter] = useState(saved?.insuranceFilter ?? "");
  const [repartoFilter, setRepartoFilter] = useState(saved?.repartoFilter ?? "");
  // Filtro stato GHL: applicato client-side sui campi `live_status`.
  // Valori: "" (tutti) | "open" | "won" | "lost" | "abandoned" | "deleted".
  const [statusFilter, setStatusFilter] = useState(saved?.statusFilter ?? "");
  const [search, setSearch] = useState(saved?.search ?? "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  // Tracking del bottone "Cancella" attivo (opportunity_id) per disabilitare
  // e mostrare lo spinner solo sulla riga in corso di cancellazione.
  const [deleting, setDeleting] = useState("");
  // Selezione multipla per il flusso "Cancella selezionati" (bulk soft-delete).
  // Set<opportunity_id>. Si svuota su cambio filtri/data/refresh (come
  // `pendingDups`) e dopo il bulk delete. Visibile solo a chi ha il
  // flag `indirettiShortcut` (admin o utenti abilitati). Le opp già
  // cancellate non sono selezionabili.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Staging duplicazioni RIMOSSO il 04/06/2026 (richiesta utente). Il
  // flusso "duplica → Indiretti" è stato sostituito dallo split inline
  // nel piano di cura editabile (dropdown per riga in Odontogram.jsx +
  // backend `/api/ghl/opportunities/{id}/split-indiretti`).

  // Persisti i filtri ogni volta che cambiano (così tornando dall'edit
  // ritroviamo lo stesso stato).
  useEffect(() => {
    try {
      sessionStorage.setItem(
        ARCHIVE_FILTERS_KEY,
        JSON.stringify({ date, doctorFilter, insuranceFilter, repartoFilter, statusFilter, search }),
      );
    } catch (err) { console.debug("archiveFilters persist failed:", err?.message || err); /* sessionStorage may be unavailable in private mode */ }
  }, [date, doctorFilter, insuranceFilter, repartoFilter, statusFilter, search]);

  // Reset selezione bulk-delete su cambio data/filtri (era condiviso
  // con il box duplicazioni rimosso).
  useEffect(() => {
    setSelectedIds(new Set());
  }, [date, doctorFilter, insuranceFilter, repartoFilter, statusFilter]);

  // Flag utente "Indiretti rapidi": gli admin l'hanno sempre, gli utenti
  // standard solo se l'admin l'ha attivato via Gestione Utenti.
  const indirettiShortcut = !!(
    authUser?.role === "admin" || authUser?.indiretti_shortcut
  );
  // Admin-only: visibilità delle opportunità cancellate. Gli USER NON
  // possono vederle in alcun modo (filtrate via, opzione "Cancellata"
  // nel dropdown stati rimossa). Gli ADMIN le hanno nascoste di default
  // ma possono attivarle col toggle "Mostra cancellate" in toolbar.
  // Persistenza in localStorage così la scelta sopravvive ai reload.
  const isAdmin = (authUser?.role || "") === "admin";
  const [showCancelled, setShowCancelled] = useState(() => {
    try {
      return isAdmin && localStorage.getItem("archive_show_cancelled") === "1";
    } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("archive_show_cancelled", showCancelled ? "1" : "0"); } catch (err) { console.debug("archive_show_cancelled persist failed:", err?.message || err); }
  }, [showCancelled]);
  // Se l'utente perde i permessi admin (es. cambio account), resetta.
  useEffect(() => {
    if (!isAdmin && showCancelled) setShowCancelled(false);
  }, [isAdmin, showCancelled]);
  // Se le cancellate vengono nascoste e il filtro stato è "Cancellata",
  // resettalo (eviterebbe una lista perennemente vuota).
  useEffect(() => {
    if (!showCancelled && statusFilter === "cancellata") setStatusFilter("");
  }, [showCancelled, statusFilter]);

  // Debounce search so we don't hammer the backend on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchItems = useCallback(async (opts) => {
    const bust = !!(opts && opts.bust);
    setLoading(true);
    try {
      const params = {};
      // When the user is searching, the backend ignores `date` so we can hit
      // older plans without the user having to clear the date filter manually.
      if (date && !debouncedSearch) params.date = date;
      if (doctorFilter) params.doctor_id = doctorFilter;
      if (insuranceFilter) params.assicurazione_id = insuranceFilter;
      if (repartoFilter) params.reparto_pipeline_id = repartoFilter;
      if (debouncedSearch) params.q = debouncedSearch;
      // Force refresh: bypass del cache TTL 30s lato backend (vedi
      // `_fetch_live_map_parallel` in server.py). Usato dal bottone
      // "Aggiorna" esplicito.
      if (bust) params.bust = Date.now();
      const { data } = await axios.get(`${API}/opportunities/created`, { params });
      const all = data?.items || [];
      // Show only true "Piano di Cura" pipelines — hide Promemoria and any
      // legacy/auxiliary pipelines so the archive stays focused on plans.
      const filtered = all.filter((it) => ARCHIVIO_PIPELINE_IDS.has(it.pipeline_id));
      setItems(filtered);    } catch (err) {
      console.error("Fetch created opportunities failed:", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [date, doctorFilter, insuranceFilter, repartoFilter, debouncedSearch]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // handleDuplicateToIndiretti / handleRemovePendingDup /
  // handleSavePendingDups RIMOSSI il 04/06/2026 (richiesta utente).
  // Il flusso duplicazione → Indiretti è stato sostituito dal dropdown
  // inline per riga nel piano di cura editabile (cfr. App.js +
  // backend `/api/ghl/opportunities/{id}/split-indiretti`).

  const handleSoftDelete = useCallback(async (item) => {
    if (deleting) return;
    const patientLabel = item.contact_name || "questo paziente";
    if (!window.confirm(
      `Cancellare l'opportunità di ${patientLabel}?\n\n` +
      "L'opportunità verrà ELIMINATA da GoHighLevel. La riga resterà " +
      "visibile in Archivio marcata come Cancellata (importo barrato in " +
      "rosso). L'operazione è tracciata nel log di audit.\n\n" +
      "Procedere?",
    )) {
      return;
    }
    setDeleting(item.opportunity_id);
    try {
      await axios.delete(
        `${API}/appointments/opportunities/${item.opportunity_id}/soft-delete`,
        { withCredentials: true, timeout: 30000 },
      );
      toast.success(`Opportunità di ${patientLabel} cancellata su GHL`);
      await fetchItems();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Errore nella cancellazione dell'opportunità",
      );
    } finally {
      setDeleting("");
    }
  }, [deleting, fetchItems]);

  // Toggle selezione singola riga (Set di opportunity_id). Le opp già
  // cancellate non sono selezionabili (filtrate nell'UI), ma la guardia
  // qui è defense-in-depth.
  const toggleSelect = useCallback((opp_id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(opp_id)) next.delete(opp_id);
      else next.add(opp_id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  // Cancellazione bulk: invia in sequenza N DELETE soft-delete (NON in
  // parallelo per evitare rate-limit GHL). Mostra una sola conferma
  // riassuntiva, poi toast per esiti aggregati. Le opp con errore
  // restano selezionate per facilitare retry.
  const handleBulkDelete = useCallback(async (selectableItems) => {
    if (bulkDeleting) return;
    const toDelete = selectableItems.filter((it) => selectedIds.has(it.opportunity_id));
    if (toDelete.length === 0) return;
    const names = toDelete.slice(0, 5).map((it) => it.contact_name || "Senza nome").join(", ");
    const more = toDelete.length > 5 ? ` e altri ${toDelete.length - 5}` : "";
    if (!window.confirm(
      `Cancellare ${toDelete.length} opportunità?\n\n` +
      `${names}${more}\n\n` +
      "Tutte queste opportunità verranno ELIMINATE da GoHighLevel. " +
      "Le righe resteranno visibili in Archivio marcate come Cancellate. " +
      "L'operazione è tracciata nel log di audit.\n\n" +
      "Procedere?",
    )) {
      return;
    }
    setBulkDeleting(true);
    const okIds = new Set();
    const failedIds = new Set();
    for (const it of toDelete) {
      try {
        await axios.delete(
          `${API}/appointments/opportunities/${it.opportunity_id}/soft-delete`,
          { withCredentials: true, timeout: 30000 },
        );
        okIds.add(it.opportunity_id);
      } catch (err) {
        console.error(`bulk-delete failed for ${it.opportunity_id}:`, err);
        failedIds.add(it.opportunity_id);
      }
    }
    if (okIds.size) toast.success(`${okIds.size} opportunità cancellate su GHL`);
    if (failedIds.size) {
      toast.error(`${failedIds.size} cancellazione/i fallita/e — restano selezionate per riprovare`);
    }
    // Lascia selezionate solo le opp fallite (per retry); rimuovi le altre.
    setSelectedIds(failedIds);
    setBulkDeleting(false);
    await fetchItems();
  }, [bulkDeleting, selectedIds, fetchItems]);

  // Distinct filter options derived from current items + global lookups
  const doctorOptions = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      if (it.medico_id) map.set(it.medico_id, it.medico_name || it.medico_id);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const insuranceOptions = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      if (it.assicurazione_id) map.set(it.assicurazione_id, it.assicurazione_name || it.assicurazione_id);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  const repartoOptions = useMemo(() => {
    const map = new Map();
    items.forEach((it) => {
      if (it.pipeline_id) map.set(it.pipeline_id, repartoLabel(it.pipeline_id));
    });
    return Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [items]);

  // Per gli admin: apre in editabile (admin bypass sulla matrice).
  // Per gli user: apre in base ai permessi stage (canEdit → editable;
  // canVision → consult-only; nessuno → toast "Permesso negato").
  // Richiesta utente 06/06/2026: matrice stage_permissions trasversale.
  const handleEdit = (opp, readonly = false) => {
    const currentStageId = opp.live_stage_id || opp.stage_id;
    const stagePerm = getStagePermission(authUser, opp.pipeline_id, currentStageId);
    if (!stagePerm.vision && !stagePerm.edit) {
      toast.error("Permesso negato per questo stage");
      return;
    }
    const mode = PIPELINE_TO_MODE[opp.pipeline_id] || "clinico";
    const params = new URLSearchParams({ opportunityId: opp.opportunity_id });
    if (readonly) params.set("readonly", "1");
    if (!stagePerm.edit) params.set("consult", "1");
    // Marker letto da Navbar (per mostrare X in readonly) e da App.js
    // (per redirect a /opportunita-create dopo il salvataggio).
    try { sessionStorage.setItem("archiveReturnActive", "1"); } catch (err) { console.debug("archiveReturnActive set failed:", err?.message || err); }
    navigate(`/piano-cura-${mode}?${params.toString()}`);
  };

  // Storico stage modal rimosso (richiesta utente Feb 2026): il pulsante
  // "Storico" è stato rimosso dalla riga dell'archivio per dare più spazio
  // ai dati principali. Lo storico stage resta consultabile da altre pagine.
  // Apply the client-side "Stato" filter. Done after fetch because the
  // backend already returns the live_status enrichment in the same payload
  // Mappa stato GHL → categoria UI ("pagata" | "cancellata" | "open").
  // - won            → Pagata (verde)
  // - lost/abandoned → Cancellata (rosso)
  // - deleted        → Cancellata (rosso)
  // - open           → Aperta + stage live
  const mapStatus = (s) => {
    if (s === "won") return "pagata";
    if (s === "lost" || s === "abandoned" || s === "deleted") return "cancellata";
    return s || "";
  };

  // — no extra round-trip needed, and switching the filter is instant.
  const visibleItems = useMemo(() => {
    let arr = items;
    // Hide cancellate per default: USER non le vede MAI; ADMIN le
    // nasconde tranne quando ha attivato il toggle "Mostra cancellate".
    if (!isAdmin || !showCancelled) {
      arr = arr.filter((it) => mapStatus(it.live_status || "") !== "cancellata");
    }
    if (!statusFilter) return arr;
    return arr.filter((it) => mapStatus(it.live_status || "") === statusFilter);
  }, [items, statusFilter, isAdmin, showCancelled]);

  const totalSum = useMemo(
    () => visibleItems.reduce((acc, it) => acc + (it.total_cost || 0), 0),
    [visibleItems]
  );

  // Opp selezionabili per il bulk delete: NON cancellate (live_status !==
  // lost/abandoned/deleted). Usato per il "select all" e per il bottone.
  const selectableVisibleItems = useMemo(
    () => visibleItems.filter((it) => {
      const s = it.live_status || "";
      return s !== "lost" && s !== "abandoned" && s !== "deleted";
    }),
    [visibleItems],
  );
  const selectedCount = selectedIds.size;
  const allVisibleSelected = selectableVisibleItems.length > 0 &&
    selectableVisibleItems.every((it) => selectedIds.has(it.opportunity_id));
  const someVisibleSelected = selectedCount > 0 && !allVisibleSelected;

  // Select-all checkbox: spunta tutte le opp selezionabili in vista;
  // se sono già tutte spuntate, le deseleziona.
  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allHere = selectableVisibleItems.every((it) => next.has(it.opportunity_id));
      if (allHere) {
        selectableVisibleItems.forEach((it) => next.delete(it.opportunity_id));
      } else {
        selectableVisibleItems.forEach((it) => next.add(it.opportunity_id));
      }
      return next;
    });
  }, [selectableVisibleItems]);

  return (
    <div className="min-h-screen bg-gray-100 bg-grid-light pt-[calc(7px+3.5rem+13px)] pb-10 px-4 lg:px-[100px]">
      <div className="w-full">
        <PageHeader
          title="Piani di Cura — Archivio"
          subtitle="Riepilogo giornaliero · clicca su una riga per aggiornare il piano di cura"
          actions={
            <button
              type="button"
              onClick={() => fetchItems({ bust: true })}
              data-testid="refresh-opportunita"
              title="Aggiorna lista (forza fetch da GHL)"
              aria-label="Aggiorna"
              className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white transition-colors backdrop-blur-sm"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          }
        />
      </div>

      <div className="w-full mb-6 -mt-8 relative z-10">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 space-y-3">
          {/* Search across the entire archive */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Cerca per nome o cognome (inizio parola)…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-archive"
              className="h-11 w-full pl-9 pr-9 text-sm border border-slate-200 rounded-lg bg-white text-slate-700 focus:outline-none focus:border-blue-400"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                aria-label="Pulisci ricerca"
                data-testid="clear-archive-search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {debouncedSearch && (
            <div className="text-[11px] text-slate-500">
              Ricerca attiva &quot;<strong className="text-slate-700">{debouncedSearch}</strong>&quot; · il filtro data viene ignorato durante la ricerca.
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="filter-date"
                disabled={!!debouncedSearch}
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
            <div className="flex items-center gap-2">
              <Stethoscope className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <select
                value={doctorFilter}
                onChange={(e) => setDoctorFilter(e.target.value)}
                data-testid="filter-doctor"
                className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
              >
                <option value="">Tutti i medici</option>
                {doctorOptions.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <select
              value={insuranceFilter}
              onChange={(e) => setInsuranceFilter(e.target.value)}
              data-testid="filter-insurance"
              className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
            >
              <option value="">Tutte le assicurazioni</option>
              {insuranceOptions.map(([id, name]) => (
                <option key={id} value={id}>{name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <select
              value={repartoFilter}
              onChange={(e) => setRepartoFilter(e.target.value)}
              data-testid="filter-reparto"
              className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
            >
              <option value="">Tutti i reparti</option>
              {repartoOptions.map(([pid, name]) => (
                <option key={pid} value={pid}>{name}</option>
              ))}
            </select>
          </div>
          {/* Status filter (client-side, derived from live_status enrichment) */}
          <div className="flex items-center gap-2">
            <CircleDot className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              data-testid="filter-status"
              className="h-10 w-full text-sm border border-slate-200 rounded-lg px-3 bg-white text-slate-700 focus:outline-none focus:border-blue-400"
            >
              <option value="">Tutti gli stati</option>
              <option value="open">Aperta</option>
              <option value="pagata">Pagata</option>
              {isAdmin && showCancelled && (
                <option value="cancellata">Cancellata</option>
              )}
            </select>
          </div>
        </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-3 text-slate-500">
            <span className="flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" /> <strong className="text-slate-700">{visibleItems.length}</strong>
              {statusFilter && items.length !== visibleItems.length ? ` / ${items.length}` : ""} opportunità
            </span>
            <span className="font-semibold text-slate-700">Totale: {fmtEuro(totalSum)}</span>
            {/* Admin-only: toggle visibilità cancellate. Gli USER non
                vedono mai questo bottone né le opp cancellate. */}
            {isAdmin && (
              <button
                type="button"
                onClick={() => setShowCancelled((v) => !v)}
                data-testid="toggle-show-cancelled"
                title={showCancelled
                  ? "Nascondi le opportunità cancellate dall'archivio"
                  : "Mostra anche le opportunità cancellate nell'archivio"}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                  showCancelled
                    ? "bg-red-50 border-red-300 text-red-700 hover:bg-red-100"
                    : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50"
                }`}
              >
                {showCancelled ? <Eye className="w-3 h-3" /> : <X className="w-3 h-3" />}
                {showCancelled ? "Cancellate visibili" : "Cancellate nascoste"}
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={() => { setDoctorFilter(""); setInsuranceFilter(""); setRepartoFilter(""); setStatusFilter(""); setDate(today); }}
            className="text-blue-600 hover:underline"
          >
            Reset filtri
          </button>
        </div>
        {/* Bulk-delete toolbar — visibile SOLO con flag "Indiretti rapidi"
            (stesso permesso del cestino singolo) e SOLO quando ≥ 1 opp
            è selezionata. Sequenziale (no parallelo) per evitare
            rate-limit GHL. Richiesta utente 31/05/2026. */}
        {indirettiShortcut && selectedCount > 0 && (
          <div
            className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border-2 border-red-300 bg-red-50 px-3 py-2"
            data-testid="bulk-delete-bar"
          >
            <div className="flex items-center gap-3 text-sm text-red-900">
              <CheckCircle2 className="w-4 h-4 text-red-600" />
              <strong data-testid="bulk-selected-count">{selectedCount}</strong>
              {selectedCount === 1 ? " opportunità selezionata" : " opportunità selezionate"}
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={clearSelection}
                disabled={bulkDeleting}
                data-testid="bulk-clear-selection"
                className="h-8 px-3 text-xs border-slate-300 text-slate-700 hover:bg-white"
              >
                Annulla
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => handleBulkDelete(selectableVisibleItems)}
                disabled={bulkDeleting}
                data-testid="bulk-delete-button"
                className="h-8 px-3 text-xs bg-red-600 hover:bg-red-700 text-white"
              >
                {bulkDeleting
                  ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Cancellazione in corso...</>
                  : <><Trash2 className="w-3.5 h-3.5 mr-1" /> Cancella selezionati ({selectedCount})</>}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Box "Anteprima duplicazioni" RIMOSSO il 04/06/2026 (richiesta
          utente). Lo spostamento in Indiretti avviene ora INLINE nel
          piano di cura editabile tramite il dropdown "→ Indiretti Reparto
          Clinico/Ortodonzia" su ogni riga prestazione. Cfr. App.js +
          Odontogram.jsx + backend `/api/ghl/opportunities/{id}/split-indiretti`. */}

      <div className="w-full">
        {loading ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <Loader2 className="w-8 h-8 text-slate-300 animate-spin mx-auto mb-3" />
            <p className="text-slate-400">Caricamento opportunità…</p>
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="bg-white rounded-3xl shadow-md p-12 text-center">
            <CalendarIcon className="w-12 h-12 text-slate-200 mx-auto mb-3" />
            <p className="text-slate-400 text-lg">Nessuna opportunità trovata per questi filtri</p>
            <p className="text-slate-300 text-xs mt-2">Le opportunità vengono tracciate solo dalla data di rilascio della funzionalità</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className={`hidden sm:grid ${indirettiShortcut
              ? "grid-cols-[36px_90px_minmax(0,1.6fr)_minmax(0,1fr)_125px_125px_95px_45px_360px]"
              : "grid-cols-[90px_minmax(0,1.6fr)_minmax(0,1fr)_125px_125px_95px_45px_360px]"
            } gap-3 px-4 py-3 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400`}>
              {indirettiShortcut && (
                <div className="flex items-center justify-center">
                  {/* Select-all checkbox: spunta/deseleziona tutte le opp
                      selezionabili in vista. `indeterminate` quando solo
                      alcune sono selezionate. */}
                  <input
                    type="checkbox"
                    aria-label="Seleziona tutte le opportunità visibili"
                    checked={allVisibleSelected}
                    ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                    onChange={toggleSelectAllVisible}
                    disabled={selectableVisibleItems.length === 0}
                    data-testid="bulk-select-all"
                    className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                  />
                </div>
              )}
              <div>Ora</div>
              <div>Paziente</div>
              <div>Assicurazione · Reparto</div>
              <div>Medico</div>
              <div>Stato</div>
              <div className="text-right">Totale</div>
              <div className="text-center">Rev.</div>
              <div className="text-right">Azioni</div>
            </div>

            {visibleItems.map((it) => {
              const _mode = PIPELINE_TO_MODE[it.pipeline_id] || "clinico";
              const created = it.created_at ? new Date(it.created_at) : null;
              const updated = it.last_updated_at ? new Date(it.last_updated_at) : null;
              const wasUpdated = (it.revision_count || 1) > 1;
              // Map GHL `live_status` → badge categoriale.
              //   "won"                       → Pagata (emerald)
              //   "lost" / "abandoned" / "deleted" → Cancellata (red)
              //   "open"                      → live_stage_name (blue)
              const status = it.live_status || "";
              const cat = (() => {
                if (status === "won") return "pagata";
                if (status === "lost" || status === "abandoned" || status === "deleted") return "cancellata";
                return status;
              })();
              const ghlStateLabel = {
                won: "Vinta su GHL",
                lost: "Persa su GHL",
                abandoned: "Abbandonata su GHL",
                deleted: "Cancellata su GHL",
              }[status] || "";
              const statusBadge = (() => {
                if (cat === "cancellata") return { label: "Cancellata", cls: "bg-red-100 text-red-700 border border-red-200" };
                if (cat === "pagata") return { label: "Pagata", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200" };
                if (cat === "open") return { label: it.live_stage_name || "Aperta", cls: "bg-blue-50 text-blue-700 border border-blue-200" };
                return null;
              })();
              return (
                <div
                  key={it.opportunity_id}
                  className={`flex flex-col gap-2 sm:gap-3 sm:grid ${indirettiShortcut
                    ? "sm:grid-cols-[36px_90px_minmax(0,1.6fr)_minmax(0,1fr)_125px_125px_95px_45px_360px]"
                    : "sm:grid-cols-[90px_minmax(0,1.6fr)_minmax(0,1fr)_125px_125px_95px_45px_360px]"
                  } px-3 py-3 sm:px-4 border-b border-slate-50 last:border-b-0 hover:bg-slate-50/50 transition-colors ${
                    cat === "cancellata" ? "bg-red-50/30" : ""
                  } ${selectedIds.has(it.opportunity_id) ? "bg-red-50/60" : ""}`}
                  data-testid={`opp-row-${it.opportunity_id}`}
                >
                  {/* Cell 0 (desktop only): checkbox per bulk delete.
                      SEMPRE visibile (anche per opp cancellate, per
                      allineamento visivo) ma disabilitata per le già
                      cancellate. Solo a chi ha `indirettiShortcut`. */}
                  {indirettiShortcut && (
                    <div className="hidden sm:flex items-center justify-center">
                      <input
                        type="checkbox"
                        aria-label={`Seleziona ${it.contact_name || "opportunità"}`}
                        checked={selectedIds.has(it.opportunity_id)}
                        onChange={() => toggleSelect(it.opportunity_id)}
                        disabled={cat === "cancellata"}
                        title={cat === "cancellata" ? "Opportunità già cancellata" : ""}
                        data-testid={`bulk-select-${it.opportunity_id}`}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                      />
                    </div>
                  )}
                  {/* MOBILE HEADER — solo su mobile. Paziente + Stato. */}
                  <div className="flex sm:hidden items-start justify-between gap-2">
                    {indirettiShortcut && (
                      <input
                        type="checkbox"
                        aria-label={`Seleziona ${it.contact_name || "opportunità"}`}
                        checked={selectedIds.has(it.opportunity_id)}
                        onChange={() => toggleSelect(it.opportunity_id)}
                        disabled={cat === "cancellata"}
                        title={cat === "cancellata" ? "Opportunità già cancellata" : ""}
                        data-testid={`bulk-select-mobile-${it.opportunity_id}`}
                        className="w-4 h-4 rounded border-slate-300 text-red-600 focus:ring-red-500 cursor-pointer mt-1 disabled:cursor-not-allowed disabled:opacity-30"
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800 truncate">{it.contact_name || "—"}</div>
                      <div className="text-[10px] text-slate-500 tabular-nums mt-0.5">
                        {created ? created.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"}
                        <span className="ml-2 inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-700">
                          v{it.revision_count || 1}
                        </span>
                      </div>
                    </div>
                    {statusBadge && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold shrink-0 ${statusBadge.cls}`}
                        title={cat === "cancellata" ? (ghlStateLabel || "Cancellata") : statusBadge.label}
                      >
                        {statusBadge.label}
                      </span>
                    )}
                  </div>

                  {/* MOBILE INFO — assicurazione/reparto/medico/totale in 2 colonne. */}
                  <div className="grid grid-cols-2 gap-2 sm:hidden text-xs">
                    <div className="min-w-0">
                      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Assicurazione</div>
                      <div className="text-slate-700 truncate">
                        {isIndiretto(it.pipeline_id) ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 rounded">
                            Indiretto
                          </span>
                        ) : (it.assicurazione_name || "—")}
                      </div>
                      <div className="text-slate-400 truncate text-[11px]">{repartoLabel(it.pipeline_id)}</div>
                    </div>
                    <div className="min-w-0 text-right">
                      <div className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Totale</div>
                      <div className={`text-sm font-bold tabular-nums ${
                        cat === "cancellata" ? "text-red-600 line-through" : "text-emerald-700"
                      }`}>{fmtEuro(it.total_cost)}</div>
                      <div className="text-slate-500 truncate text-[11px]">{it.medico_name || "—"}</div>
                    </div>
                  </div>

                  {/* DESKTOP COLONNE — 8-col grid. */}
                  <div className="hidden sm:block text-xs">
                    <div className="font-semibold text-slate-700 tabular-nums">
                      {created ? created.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}
                    </div>
                    <div className="text-slate-400 text-[10px]">
                      {created ? created.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" }) : ""}
                    </div>
                  </div>
                  <div className="hidden sm:block min-w-0">
                    <div className="text-sm font-semibold text-slate-800 truncate">{it.contact_name || "—"}</div>
                    {it.created_by_email && (
                      <div className="text-[11px] text-slate-400 truncate">creata da {it.created_by_email}</div>
                    )}
                  </div>
                  <div className="hidden sm:block min-w-0 text-xs">
                    <div className="text-slate-700 truncate">
                      {isIndiretto(it.pipeline_id) ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 rounded">
                          Indiretto
                        </span>
                      ) : (it.assicurazione_name || "—")}
                    </div>
                    <div className="text-slate-400 truncate">{repartoLabel(it.pipeline_id)}</div>
                  </div>
                  <div className="hidden sm:block text-xs text-slate-600 truncate">{it.medico_name || "—"}</div>
                  <div className="hidden sm:block min-w-0 text-xs">
                    {statusBadge ? (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold truncate max-w-full ${statusBadge.cls}`}
                        title={
                          cat === "cancellata"
                            ? (ghlStateLabel || "Opportunità cancellata")
                            : cat === "open"
                            ? `Stage attuale in GHL: ${statusBadge.label}`
                            : cat === "pagata"
                            ? "Opportunità vinta su GHL"
                            : `Stato GHL: ${statusBadge.label}`
                        }
                        data-testid={`opp-status-${it.opportunity_id}`}
                      >
                        {statusBadge.label}
                      </span>
                    ) : (
                      <span className="text-slate-300">—</span>
                    )}
                  </div>
                  <div className={`hidden sm:block text-sm font-bold tabular-nums sm:text-right ${
                    cat === "cancellata" ? "text-red-600 line-through" : "text-emerald-700"
                  }`}>
                    {fmtEuro(it.total_cost)}
                  </div>
                  <div className="hidden sm:block sm:text-center text-xs">
                    <span
                      className={`inline-flex items-center justify-center px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        wasUpdated ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"
                      }`}
                      title={updated ? `Ultima modifica: ${updated.toLocaleString("it-IT")}` : ""}
                    >
                      v{it.revision_count || 1}
                    </span>
                  </div>
                  <div className="flex flex-nowrap items-center justify-start sm:justify-end gap-1.5 pt-1 sm:pt-0 border-t border-slate-100 sm:border-t-0">
                    {/* GRID FISSO a 6 colonne — ogni opp ha gli stessi 6
                        slot, anche se vuoti, per allineare verticalmente i
                        bottoni fra una riga e l'altra. Ordine richiesto
                        dall'utente (Feb-May 2026):
                          1. 👁  Eye — apre l'opp in sola lettura (sempre)
                          2. 📷  Foto — solo per OPEN + stage non-editabile
                          3. ✏️  Aggiorna — OPEN+editable + flag "Indiretti rapidi"
                          4. 📋  Clinico — duplica indiretti R.C. (flag richiesto)
                          5. 📋  Orto    — duplica indiretti Orto    (flag richiesto)
                          6. 🗑  Cancella — soft-delete GHL (flag richiesto + non già cancellata)
                        Slot 3/6 condividono la stessa autorizzazione di
                        slot 4/5: il flag "Indiretti rapidi" (admin sempre,
                        user solo se l'admin l'ha attivato in Gestione Utenti).
                        Slot vuoti = `invisible` placeholder per mantenere
                        la stessa griglia visiva ovunque. */}

                    {/* Slot 1: Icona dinamica basata sul permesso di stage:
                        - canEdit → pennino blu (modifica)
                        - canVision (no edit) → occhio grigio (consulta)
                        - nessun permesso → lucchetto rosso (permesso negato)
                        Gli admin hanno sempre canEdit=true.
                        Usiamo `live_stage_id` (lo stage corrente in GHL,
                        aggiornato dall'enrich di server.py:1193) come
                        chiave primaria. Fallback su `stage_id` per
                        backward compat. */}
                    {(() => {
                      const currentStageId = it.live_stage_id || it.stage_id;
                      const stagePerm = getStagePermission(authUser, it.pipeline_id, currentStageId);
                      const canEdit = stagePerm.edit;
                      const canVision = stagePerm.vision || stagePerm.edit;
                      const locked = !canVision && !canEdit;
                      return (
                        <Button
                          size="sm"
                          variant={canEdit ? "default" : "outline"}
                          onClick={() => handleEdit(it)}
                          data-testid={canEdit ? `edit-opp-${it.opportunity_id}` : (canVision ? `view-opp-${it.opportunity_id}` : `locked-opp-${it.opportunity_id}`)}
                          title={
                            locked
                              ? "Permesso negato per questo stage"
                              : canEdit ? "Modifica piano di cura" : "Consulta piano di cura"
                          }
                          className={`h-8 w-9 px-0 flex-shrink-0 ${
                            locked
                              ? "border-red-300 text-red-700 hover:bg-red-50"
                              : canEdit
                                ? "bg-[#1a1fa8] hover:bg-[#3B42DD] text-white"
                                : "border-slate-300 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                          }`}
                        >
                          {locked ? <Lock className="w-3.5 h-3.5" /> : canEdit ? <Edit3 className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </Button>
                      );
                    })()}

                    {/* Slot 2: Foto (camera icon only) — OPEN + non-editabile */}
                    {(status === "" || status === "open") &&
                      !isPianoEditable(it.pipeline_id, it.live_stage_name) ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(it)}
                        data-testid={`consult-locked-opp-${it.opportunity_id}`}
                        title={`Piano in sola consultazione — stage attuale: ${it.live_stage_name || "—"}. Puoi ancora caricare foto.`}
                        className="h-8 w-9 px-0 flex-shrink-0 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                      >
                        <Camera className="w-3.5 h-3.5" />
                      </Button>
                    ) : (
                      <span aria-hidden className="inline-block h-8 w-9 flex-shrink-0" />
                    )}

                    {/* Slot 3 RIMOSSO il 06/06/2026 — il pennino "Aggiorna"
                        è stato unificato con lo slot 1 (sempre pennino).
                        Placeholder mantenuto per allineamento layout. */}
                    <span aria-hidden className="inline-block h-8 w-9 flex-shrink-0" />

                    {/* Slot 4-5: Duplica → Clinico/Orto RIMOSSI il
                        04/06/2026 (richiesta utente). Lo spostamento in
                        Indiretti avviene ora INLINE nel piano di cura
                        editabile tramite il dropdown "→ Indiretti Reparto
                        Clinico/Ortodonzia" su ogni riga prestazione.
                        Manteniamo gli slot vuoti per allineamento layout. */}
                    <span aria-hidden className="inline-block h-8 w-[72px] flex-shrink-0" />
                    <span aria-hidden className="inline-block h-8 w-[72px] flex-shrink-0" />

                    {/* Slot 6: Cancella su GHL (soft-delete) — solo per
                        opportunità NON già cancellate. Apre un popup
                        nativo di conferma prima di chiamare l'API.
                        Vincolato al flag "Indiretti rapidi" (admin sempre,
                        user solo se l'admin l'ha attivato): le stesse
                        autorizzazioni del pennino "Aggiorna". */}
                    {cat !== "cancellata" && indirettiShortcut ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSoftDelete(it)}
                        disabled={deleting === it.opportunity_id}
                        data-testid={`delete-opp-${it.opportunity_id}`}
                        title="Cancella opportunità su GoHighLevel"
                        className="h-8 w-9 px-0 flex-shrink-0 border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        {deleting === it.opportunity_id
                          ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          : <Trash2 className="w-3.5 h-3.5" />}
                      </Button>
                    ) : (
                      <span aria-hidden className="inline-block h-8 w-9 flex-shrink-0" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
